import {headerEventsConfig} from './datalayerConfig/headerEventsConfig.js';
import {searchEventsConfig} from './datalayerConfig/searchEventsConfig.js';
import {headerSearchEventsConfig} from './datalayerConfig/headerSearchEventsConfig.js'; // Add new import
import {searchPageEventsConfig} from './datalayerConfig/searchPageEventsConfig.js'; // Add new import
import {menuEventsConfig} from './datalayerConfig/menuEventsConfig.js';
import {pdpEventsConfig} from './datalayerConfig/pdpEventsConfig.js';
import {collectionProductEventsConfig} from './datalayerConfig/collectionProductEventsConfig.js'; // Import collection config
import {searchProductEventsConfig} from './datalayerConfig/searchProductEventsConfig.js'; // Import search product config
import {cartEventsConfig} from './datalayerConfig/cartEventsConfig.js';
import {pageEventsConfig} from './datalayerConfig/pageEventsConfig.js';
import {formEventsConfig} from './datalayerConfig/formEventsConfig.js';
import {loginEventsConfig} from './datalayerConfig/loginEventsConfig.js';
import {registerEventsConfig} from './datalayerConfig/registerEventsConfig.js';
import {footerEventsConfig} from './datalayerConfig/footerEventsConfig.js';

// Datalayer object to store events
window.dataLayer = window.dataLayer || [];

// Function to push events to dataLayer
function pushToDataLayer(event) {
    window.dataLayer.push(event);
}

// Function to check if element has a specific parent
function hasParent(element, parentSelector) {
    let currentElement = element;
    while (currentElement) {
        if (currentElement.matches(parentSelector)) {
            return true;
        }
        currentElement = currentElement.parentElement;
    }
    return false;
}

// Debounce function for input events
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

// Find the matching configuration for an element
function findMatchingConfig(element, eventType, configs) {
    for (const [key, value] of Object.entries(configs.events)) {
        // Check for single event configuration
        if (value.event && value.event.trigger === eventType) {
            if (element.matches(value.selector)) {
                // Check parent selector if needed
                if (configs.parentSelector && !hasParent(element, configs.parentSelector)) {
                    continue;
                }
                return { configKey: key, config: value.event, element };
            }
        }
        
        // Check for multiple events configuration
        if (value.events) {
            for (const [eventKey, eventConfig] of Object.entries(value.events)) {
                if (eventConfig.trigger === eventType) {
                    const selector = eventConfig.selector || value.selector;
                    if (element.matches(selector)) {
                        // Check parent selector if needed
                        if (configs.parentSelector && !hasParent(element, configs.parentSelector)) {
                            continue;
                        }
                        return { configKey: `${key}.${eventKey}`, config: eventConfig, element };
                    }
                }
            }
        }
    }
    return null;
}

// Process the event with the matching configuration
async function processEvent(match, event) {
    if (!match) return;
    
    // Define a base structure with all potential fields initialized to empty strings
    // Customize this list based on ALL parameters used across ALL your config files
    const baseEventData = {
        component: '',
        element: '',
        field: '',
        action: '',
        action_value: '',
        link_text: '',
        link_url: '',
        product_name: '',
        search_term: '',
        field_name: '',
        field_value: '',
        error_message: '',
        select_value: '',
        click_text: '',
        // Add any other parameters used in your configurations here
    };

    // Extract static data from the config, removing internal properties
    const staticConfigData = { ...match.config };
    delete staticConfigData.getDynamicData;
    delete staticConfigData.trigger;
    // Optionally remove selector if it shouldn't be pushed
    // delete staticConfigData.selector; 

    // Get dynamic data, handling null return for conditional events (like visible errors)
    let dynamicData = {};
    if (match.config.getDynamicData) {
        const result = match.config.getDynamicData(match.element);
        if (result instanceof Promise) {
            dynamicData = await result;
        } else {
            dynamicData = result;
        }
    }
    
    if (dynamicData === null) {
        return; // Don't push event if getDynamicData explicitly returns null
    }

    // Merge data in the correct order: dynamic data should override static data, which should override base data
    const eventData = {
        ...baseEventData,
        ...staticConfigData,
        ...dynamicData,
        // Ensure these specific fields are preserved from dynamic data if they exist
        ...(dynamicData.errors && { errors: dynamicData.errors }),
        ...(dynamicData.action_value && { action_value: dynamicData.action_value })
    };
    
    // Ensure 'event' field is present (usually comes from staticConfigData)
    if (!eventData.event) {
        console.warn('Datalayer event is missing the "event" property:', match);
        return; // Don't push if 'event' name is missing
    }

    pushToDataLayer(eventData);
}

// Initialize delegated event listeners
function initializeDelegatedEvents() {
    console.log('Initializing datalayer with event delegation');
    
    // Define the list of configurations
    const configs = [
        headerEventsConfig, 
        searchEventsConfig,
        headerSearchEventsConfig,
        searchPageEventsConfig,
        menuEventsConfig, 
        pdpEventsConfig, 
        searchProductEventsConfig,
        cartEventsConfig, 
        collectionProductEventsConfig,
        footerEventsConfig,
        pageEventsConfig, 
        formEventsConfig, 
        loginEventsConfig,
        registerEventsConfig 
    ];

    // Handle click events
    document.addEventListener('click', async (event) => {
        // Get the actual target from the shadow root if the event is composed
        const target = event.composedPath()[0] || event.target;
        // console.log('Click event triggered:', {
        //     target: target,
        //     targetId: target.id,
        //     targetClass: target.className
        // });
        
        for (const config of configs) {
            const match = findMatchingConfig(target, 'click', config);
            if (match) {
                await processEvent(match, event);
                break;
            }
        }
    });
    
    // Handle focus events
    document.addEventListener('focus', async (event) => {
        // Get the actual target from the shadow root if the event is composed
        const target = event.composedPath()[0] || event.target;
        // console.log('Focus event triggered:', {
        //     target: target,
        //     targetId: target.id,
        //     targetClass: target.className
        // });
        
        for (const config of configs) {
            const match = findMatchingConfig(target, 'focus', config);
            if (match) {
                await processEvent(match, event);
                break;
            }
        }
    }, true);
    
    // Handle blur events
    document.addEventListener('blur', async (event) => {
        // Get the actual target from the shadow root if the event is composed
        const target = event.composedPath()[0] || event.target;
        // console.log('Blur event triggered:', {
        //     target: target,
        //     targetId: target.id,
        //     targetClass: target.className
        // });
        
        for (const config of configs) {
            const match = findMatchingConfig(target, 'blur', config);
            if (match) {
                await processEvent(match, event);
                break;
            }
        }
    }, true);
    
    // Handle input events with delay
    let inputTimeout;
    const inputHandler = (event) => {
        // Store the event data we need
        const path = event.composedPath();
        const target = path.find(element => 
            element instanceof HTMLInputElement || 
            element instanceof HTMLTextAreaElement || 
            element instanceof HTMLSelectElement
        ) || event.target;
        
        // Clear any existing timeout
        if (inputTimeout) {
            clearTimeout(inputTimeout);
        }
        
        // Set new timeout
        inputTimeout = setTimeout(() => {
            // console.log('Input event triggered:', {
            //     event: event,
            //     composedPath: path,
            //     target: target,
            //     activeElement: document.activeElement
            // });
            
            for (const config of configs) {
                const match = findMatchingConfig(target, 'input', config);
                if (match) {
                    processEvent(match, event);
                    break;
                }
            }
        }, 3000);
    };
    
    // Listen for both focusin and input events
    document.addEventListener('focusin', (event) => {
        const target = event.composedPath()[0] || event.target;
        // console.log('Focusin event triggered:', {
        //     target: target,
        //     targetId: target.id,
        //     targetClass: target.className
        // });
        
        for (const config of configs) {
            const match = findMatchingConfig(target, 'focusin', config);
            if (match) {
                processEvent(match, event);
                break;
            }
        }
    });

    document.addEventListener('input', inputHandler, {composed: true});
    
    // Handle change events
    document.addEventListener('change', async (event) => {
        // Get the actual target from the shadow root if the event is composed
        const target = event.composedPath()[0] || event.target;
        // console.log('Change event triggered:', {
        //     target: target,
        //     targetId: target.id,
        //     targetClass: target.className
        // });
        
        for (const config of configs) {
            const match = findMatchingConfig(target, 'change', config);
            if (match) {
                await processEvent(match, event);
                break;
            }
        }
    }, true);
    
    // Handle submit events
    document.addEventListener('submit', (event) => {
        // Get the actual target from the shadow root if the event is composed
        const target = event.composedPath()[0] || event.target;
        // console.log('Submit event triggered:', {
        //     target: target,
        //     targetId: target.id,
        //     targetClass: target.className
        // });
        
        for (const config of configs) {
            const match = findMatchingConfig(target, 'submit', config);
            if (match) {
                processEvent(match, event);
                // Removed break; Allow multiple submit handlers if needed (e.g., form + specific button)
            }
        }
    });
}

// Initialize event listeners when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    console.log('Datalayer initialized');
    initializeDelegatedEvents();
});
